import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import EditorRemoveIcon from '@atlaskit/icon/core/migration/delete--editor-remove';
import StarFilledIcon from '@atlaskit/icon/core/migration/star-starred--star-filled';
import StarIcon from '@atlaskit/icon/core/migration/star-unstarred--star';
import { SpotlightTarget } from '@atlaskit/onboarding';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { Section } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { DeleteModal } from '@atlassian/eoc-alert-common';
import { Flex } from '@atlassian/eoc-flex';

import { SPOTLIGHT_NAMES } from '../../../common/constants';
import { useSavedSearchController } from '../../../controllers/saved-search';

import { LoadingRow } from './loading-row';
import messages from './messages';
import {
	StyledButtonItem,
	StyledExpandableSection,
	StyledExpandableSectionTitle,
	StyledMoreText,
} from './styled';

export const SavedSearches = () => {
	const { formatMessage } = useIntl();

	const {
		isOpenCreatedByMe,
		isOpenSharedWithMe,
		setIsOpenCreatedByMe,
		setIsOpenSharedWithMe,
		selectedItemId,
		handleItemClick,
		isPageLoading,
		starredItem,
		expandSharedWithMe,
		expandCreatedByMe,
		isSharedWithMeExpanded,
		isCreatedByMeExpanded,
		createdByMeSearches,
		sharedWithMeSearches,
		notVisibleCreatedByMeCount,
		notVisibleSharedWithMeCount,
		defaultFilters,
		handleStar,
		openDeleteModal,
		closeDeleteModal,
		isDeleteLoading,
		handleDeleteModalSubmit,
		isDeleteModalOpen,
	} = useSavedSearchController();

	const SpotlightWrapper = isPageLoading ? React.Fragment : SpotlightTarget;

	return (
		<UFOLoadHold name="eoc-alert-list-saved-searches-loading" hold={isPageLoading}>
			<Section title={formatMessage(messages.starred)}>
				{isPageLoading && <LoadingRow />}
				{!isPageLoading && !starredItem && (
					<div style={{ margin: `${token('space.100', '8px')} 10px` }}>
						{formatMessage(messages.starredEmpty)}
					</div>
				)}
				{!isPageLoading && starredItem && (
					<StyledButtonItem
						onClick={() => handleItemClick(starredItem, 'starred-item')}
						isSelected={starredItem.id === selectedItemId}
						iconAfter={
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<Flex className="action-wrapper">
								<Tooltip content={formatMessage(messages.unstarTooltip)}>
									<IconButton
										label={formatMessage(messages.starIconLabel)}
										icon={(iconProps) => (
											<StarFilledIcon
												{...iconProps}
												color={token('color.icon.warning', '#ffc400')}
												LEGACY_size="small"
											/>
										)}
										onClick={(event) => {
											event.stopPropagation();
											handleStar('');
										}}
										appearance="subtle"
										spacing="compact"
									/>
								</Tooltip>

								<Tooltip content={formatMessage(messages.deleteTooltip)}>
									<IconButton
										label={formatMessage(messages.deleteIconLabel)}
										icon={(iconProps) => <EditorRemoveIcon {...iconProps} LEGACY_size="small" />}
										onClick={(event) => {
											event.stopPropagation();
											openDeleteModal(starredItem.id);
										}}
										appearance="subtle"
										spacing="compact"
									/>
								</Tooltip>
							</Flex>
						}
					>
						{starredItem.name}
					</StyledButtonItem>
				)}
			</Section>
			<SpotlightWrapper name={SPOTLIGHT_NAMES.sidebarItems}>
				<Section title={formatMessage(messages.filters)}>
					{isPageLoading
						? Array.from({ length: 6 }).map((_, idx) => <LoadingRow key={idx} />)
						: Object.entries(defaultFilters).map((defaultFilter) => (
								<StyledButtonItem
									key={defaultFilter[1].name}
									onClick={() => handleItemClick(defaultFilter[1], 'default-filters')}
									isSelected={defaultFilter[1].id === selectedItemId}
								>
									{formatMessage(messages[defaultFilter[0] as keyof typeof messages])}
								</StyledButtonItem>
							))}
				</Section>
			</SpotlightWrapper>

			<StyledExpandableSection
				title={
					<StyledExpandableSectionTitle>
						{formatMessage(messages.sharedWithMe)}
					</StyledExpandableSectionTitle>
				}
				isOpen={isOpenSharedWithMe}
				onClick={() => setIsOpenSharedWithMe(!isOpenSharedWithMe)}
			>
				{isPageLoading && Array.from({ length: 2 }).map((_, idx) => <LoadingRow key={idx} />)}

				{!isPageLoading && !sharedWithMeSearches?.length && (
					<div style={{ margin: `${token('space.100', '8px')} 10px` }}>
						{formatMessage(messages.sharedWithMeEmpty)}
					</div>
				)}

				{!isPageLoading &&
					sharedWithMeSearches?.map((sharedSearch) => {
						return sharedSearch.id !== starredItem?.id ? (
							<StyledButtonItem
								key={sharedSearch.id}
								onClick={() => {
									handleItemClick(sharedSearch, 'shared-with-me');
								}}
								isSelected={sharedSearch.id === selectedItemId}
								iconAfter={
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<Flex className="action-wrapper">
										<Tooltip content={formatMessage(messages.starTooltip)}>
											<IconButton
												label={formatMessage(messages.starIconLabel)}
												icon={(iconProps) => <StarIcon {...iconProps} LEGACY_size="small" />}
												onClick={(event) => {
													event.stopPropagation();
													handleStar(sharedSearch.id);
												}}
												appearance="subtle"
												spacing="compact"
											/>
										</Tooltip>
										<Tooltip content={formatMessage(messages.deleteTooltip)}>
											<IconButton
												label={formatMessage(messages.deleteIconLabel)}
												icon={(iconProps) => (
													<EditorRemoveIcon {...iconProps} LEGACY_size="small" />
												)}
												onClick={(event) => {
													event.stopPropagation();
													openDeleteModal(sharedSearch.id);
												}}
												appearance="subtle"
												spacing="compact"
											/>
										</Tooltip>
									</Flex>
								}
							>
								{sharedSearch.name}
							</StyledButtonItem>
						) : null;
					})}
				{!isPageLoading && !isSharedWithMeExpanded && (
					<StyledMoreText onClick={expandSharedWithMe}>
						{formatMessage(messages.moreText, {
							count: notVisibleSharedWithMeCount,
						})}
					</StyledMoreText>
				)}
			</StyledExpandableSection>

			<StyledExpandableSection
				title={
					<StyledExpandableSectionTitle>
						{formatMessage(messages.createdByMe)}
					</StyledExpandableSectionTitle>
				}
				isOpen={isOpenCreatedByMe}
				onClick={() => setIsOpenCreatedByMe(!isOpenCreatedByMe)}
			>
				{isPageLoading && Array.from({ length: 2 }).map((_, idx) => <LoadingRow key={idx} />)}

				{!isPageLoading && !createdByMeSearches?.length && (
					<div style={{ margin: `${token('space.100', '8px')} 10px` }}>
						{formatMessage(messages.createdByMeEmpty)}
					</div>
				)}

				{!isPageLoading &&
					createdByMeSearches?.map((createdSearch) => {
						return createdSearch.id !== starredItem?.id ? (
							<StyledButtonItem
								key={createdSearch.id}
								onClick={() => handleItemClick(createdSearch, 'created-by-me')}
								isSelected={createdSearch.id === selectedItemId}
								iconAfter={
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<Flex className="action-wrapper">
										<Tooltip content={formatMessage(messages.starTooltip)}>
											<IconButton
												label={formatMessage(messages.starIconLabel)}
												icon={(iconProps) => <StarIcon {...iconProps} LEGACY_size="small" />}
												onClick={(event) => {
													event.stopPropagation();
													handleStar(createdSearch.id);
												}}
												appearance="subtle"
												spacing="compact"
											/>
										</Tooltip>
										<Tooltip content={formatMessage(messages.deleteTooltip)}>
											<IconButton
												label={formatMessage(messages.deleteIconLabel)}
												icon={(iconProps) => (
													<EditorRemoveIcon {...iconProps} LEGACY_size="small" />
												)}
												onClick={(event) => {
													event.stopPropagation();
													openDeleteModal(createdSearch.id);
												}}
												appearance="subtle"
												spacing="compact"
											/>
										</Tooltip>
									</Flex>
								}
							>
								{createdSearch.name}
							</StyledButtonItem>
						) : null;
					})}

				{!isPageLoading && !isCreatedByMeExpanded && (
					<StyledMoreText onClick={expandCreatedByMe}>
						{formatMessage(messages.moreText, {
							count: notVisibleCreatedByMeCount,
						})}
					</StyledMoreText>
				)}
			</StyledExpandableSection>
			<DeleteModal
				messageDescriptors={{
					title: messages.deleteModalTitle,
					description: messages.deleteModalDescription,
				}}
				isDeleteDialogOpen={isDeleteModalOpen}
				onDeleteAttemptCancel={closeDeleteModal}
				isLoading={isDeleteLoading}
				onDelete={handleDeleteModalSubmit}
			/>
		</UFOLoadHold>
	);
};
